'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useLocale } from 'next-intl';
import { useRef, useState } from 'react';
import { I18nProvider } from 'react-aria';
import { DatePicker as AdobeDatePicker, Button, Calendar, CalendarCell, CalendarGrid, DateInput, DateSegment, type DateValue, Dialog, Group, Heading, Popover } from 'react-aria-components';
import { CalendarMinimalIcon, CloseIcon } from '@shared/ui-components/svgs';

// eslint-disable-next-line import/no-unassigned-import
import './date-picker-popover.scss';
import Chevron from '@shared/ui-components/svgs/chevron';
import SvgErrorIcon from '@shared/ui-components/svgs/error-icon';
import { TimePicker } from './time-picker';
import styles from './date-picker.module.scss';
type DatePickerProps = {
  ariaLabel: string;
  className?: string;
  clearLabel?: string;
  hasError?: boolean;
  isDateFormatHintDisplayed?: boolean;
  isDateTimePicker?: boolean;
  isDisabled?: boolean;
  onChange?: (value: DateValue | null) => void;
  onFocus?: () => void;
  timeLabel?: string;
  value?: DateValue | null;
};
export const DatePicker = t0 => {
  const $ = _c(71);
  let ariaLabel;
  let className;
  let hasError;
  let onChange;
  let onFocus;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let value;
  if ($[0] !== t0) {
    ({
      ariaLabel,
      className,
      clearLabel: t1,
      hasError,
      isDateFormatHintDisplayed: t2,
      isDateTimePicker: t3,
      isDisabled: t4,
      onChange,
      onFocus,
      timeLabel: t5,
      value,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = className;
    $[3] = hasError;
    $[4] = onChange;
    $[5] = onFocus;
    $[6] = rest;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = value;
  } else {
    ariaLabel = $[1];
    className = $[2];
    hasError = $[3];
    onChange = $[4];
    onFocus = $[5];
    rest = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    value = $[12];
  }
  const clearLabel = t1 === undefined ? "Clear" : t1;
  const isDateFormatHintDisplayed = t2 === undefined ? true : t2;
  const isDateTimePicker = t3 === undefined ? false : t3;
  const isDisabled = t4 === undefined ? false : t4;
  const timeLabel = t5 === undefined ? "Time" : t5;
  const locale = useLocale();
  const granularity = isDateTimePicker ? "hour" : "day";
  const [isOpen, setIsOpen] = useState(false);
  const dateJustSelectedRef = useRef(false);
  const calendarButtonRef = useRef(null);
  const isClearButtonDisplayed = Boolean(value) && !isDisabled;
  let t6;
  if ($[13] !== onChange) {
    t6 = () => {
      onChange?.(null);
      calendarButtonRef.current?.focus();
    };
    $[13] = onChange;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleClear = t6;
  let t7;
  if ($[15] !== isDateTimePicker || $[16] !== onChange || $[17] !== value) {
    t7 = zonedDateTime => {
      if (isDateTimePicker && zonedDateTime && "set" in zonedDateTime) {
        dateJustSelectedRef.current = true;
        const previousHasTime = value !== null && value !== undefined && "hour" in value;
        if (!previousHasTime) {
          onChange?.(zonedDateTime.set({
            hour: 0,
            millisecond: 0,
            minute: 0,
            second: 0
          }));
          return;
        }
      }
      onChange?.(zonedDateTime);
    };
    $[15] = isDateTimePicker;
    $[16] = onChange;
    $[17] = value;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleChange = t7;
  let t8;
  if ($[19] !== isDisabled) {
    t8 = open => {
      if (isDisabled) {
        return;
      }
      if (!open && dateJustSelectedRef.current) {
        dateJustSelectedRef.current = false;
        return;
      }
      setIsOpen(open);
    };
    $[19] = isDisabled;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[21] !== className || $[22] !== hasError || $[23] !== isDisabled) {
    t9 = classNames(styles["date-picker"], className, {
      [styles["date-picker--disabled"]]: isDisabled,
      [styles["date-picker--has-error"]]: hasError
    });
    $[21] = className;
    $[22] = hasError;
    $[23] = isDisabled;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = value ? value : null;
  const t11 = isDateTimePicker ? isOpen && !isDisabled : undefined;
  const t12 = isDateTimePicker ? handleOpenChange : undefined;
  const t13 = value ? value : null;
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DateInput>{_temp}</DateInput>;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== hasError) {
    t15 = hasError && <SvgErrorIcon className={styles["date-picker__error-icon"]} />;
    $[26] = hasError;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== clearLabel || $[29] !== handleClear || $[30] !== isClearButtonDisplayed) {
    t16 = isClearButtonDisplayed && <button aria-label={clearLabel} className={styles["date-picker__clear-button"]} onClick={handleClear} type="button"><CloseIcon /></button>;
    $[28] = clearLabel;
    $[29] = handleClear;
    $[30] = isClearButtonDisplayed;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Button className={styles["date-picker__calendar-button"]} ref={calendarButtonRef}><CalendarMinimalIcon /></Button>;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t15 || $[34] !== t16) {
    t18 = <Group className={styles["date-picker__input-wrapper"]}>{t14}<div className={styles["date-picker__calendar-button-wrapper"]}>{t15}{t16}{t17}</div></Group>;
    $[33] = t15;
    $[34] = t16;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Button className={styles["date-picker__header-button-previous"]} slot="previous"><Chevron /></Button>;
    t20 = <Heading />;
    $[36] = t19;
    $[37] = t20;
  } else {
    t19 = $[36];
    t20 = $[37];
  }
  let t21;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Calendar><header>{t19}{t20}<Button slot="next"><Chevron /></Button></header><CalendarGrid>{_temp2}</CalendarGrid></Calendar>;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== isDateTimePicker || $[40] !== isDisabled || $[41] !== onChange || $[42] !== timeLabel || $[43] !== value) {
    t22 = isDateTimePicker && <TimePicker isDisabled={isDisabled} label={timeLabel} onChange={onChange} value={value} />;
    $[39] = isDateTimePicker;
    $[40] = isDisabled;
    $[41] = onChange;
    $[42] = timeLabel;
    $[43] = value;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t22) {
    t23 = <Popover isNonModal={true} offset={20} placement="bottom right" shouldFlip={true}><Dialog>{t21}{t22}</Dialog></Popover>;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== ariaLabel || $[48] !== granularity || $[49] !== handleChange || $[50] !== isDisabled || $[51] !== onFocus || $[52] !== rest || $[53] !== t10 || $[54] !== t11 || $[55] !== t12 || $[56] !== t13 || $[57] !== t18 || $[58] !== t23) {
    t24 = <AdobeDatePicker aria-label={ariaLabel} defaultValue={t10} granularity={granularity} hideTimeZone={true} isDisabled={isDisabled} isOpen={t11} onChange={handleChange} onFocus={onFocus} onOpenChange={t12} value={t13} {...rest}>{t18}{t23}</AdobeDatePicker>;
    $[47] = ariaLabel;
    $[48] = granularity;
    $[49] = handleChange;
    $[50] = isDisabled;
    $[51] = onFocus;
    $[52] = rest;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t13;
    $[57] = t18;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t24 || $[61] !== t9) {
    t25 = <div className={t9}>{t24}</div>;
    $[60] = t24;
    $[61] = t9;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== granularity || $[64] !== hasError || $[65] !== isDateFormatHintDisplayed) {
    t26 = !hasError && isDateFormatHintDisplayed && <div className={styles["date-picker__date-format-hint"]}><div aria-hidden={true}><AdobeDatePicker aria-label="date format" granularity={granularity} hideTimeZone={true} isDisabled={true}><DateInput>{_temp3}</DateInput></AdobeDatePicker></div></div>;
    $[63] = granularity;
    $[64] = hasError;
    $[65] = isDateFormatHintDisplayed;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== locale || $[68] !== t25 || $[69] !== t26) {
    t27 = <div><I18nProvider locale={locale}>{t25}{t26}</I18nProvider></div>;
    $[67] = locale;
    $[68] = t25;
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  return t27;
};
function _temp(segment) {
  return <DateSegment segment={segment} />;
}
function _temp2(date) {
  return <CalendarCell date={date} />;
}
function _temp3(segment_0) {
  return <DateSegment segment={segment_0} />;
}